import { useEffect, useMemo } from "react";
import { keyBy, pick } from "lodash";
import {
  Box,
  Button,
  Input,
  InputGroup,
  InputRightAddon,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { v4 as uuidv4 } from "uuid";
import type { ApolloCache } from "@apollo/client";
import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import { assertPresence } from "utils/assertions";
import { useBrandMutation, useBrandQuery } from "utils/apollo/hooks/brand";
import {
  makeClearCachedType,
  makeClearNestedListFieldCache,
} from "utils/apollo/cacheHelpers";
import Form from "components/Shared/Forms/Form";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import InlineFieldsWrapper from "components/Shared/Forms/InlineFieldsWrapper";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";
import NumberInput from "components/Shared/Forms/NumberInput";
import { DIMENSIONS_FIELDS } from "components/Shared/Forms/DimensionsFieldset";
import TiHiFieldset, {
  TI_HI_FIELDS,
} from "components/Shared/Forms/TiHiFieldset";
import EditPanelContent, {
  SaveButton,
} from "components/Products/EditPanelContent";
import { trackEvent } from "utils/segment";
import ProductLineSelect from "components/Shared/Select/ProductLineSelect";
import EnumSelect from "components/Shared/Select/EnumSelect";
import {
  DimensionInput,
  SkuComponentInput,
  SkuComponentType,
  TiHiInput,
  WeightUnit,
} from "__generated__/gql/graphql";
import { WEIGHT_UNIT_LABELS } from "components/Shared/weightUnit";
import setFieldErrorsWithDict, {
  ErrorsByFieldName,
} from "components/Shared/Forms/setFieldErrorsWithDict";
import UserError from "utils/UserError";
import NamingOptions from "components/Products/NamingOptions";
import { updateCachedProductLineSkus } from "components/Products/queries";
import ArchiveSkuButton, {
  OnArchiveSuccess,
} from "components/Products/SkuPanel/ArchiveSkuButton";
import { UNARCHIVED_STATUSES } from "components/Products/utils";
import DeleteSkuButton, { DeleteSkuData } from "./DeleteSkuButton";
import SkuComponents, { IsUpcPresentByType } from "./SkuComponents";
import {
  getInitialSkuComponents,
  SKU_COMPONENT_TYPES,
  useValidateUpcUniqueness,
} from "./SkuComponents/utils";
import SkuOrderingPlatforms from "./SkuOrderingPlatforms";
import ReadOnlyIdWrapper from "./ReadOnlyIdWrapper";
import {
  SkuPrimitiveInputs,
  skuFields,
  SkuInputs,
  skuSchema,
  ControlledFormField,
  FormField,
  skuComponentFields,
  skuBrandOrderingPlatformFields,
  SkuOrderingPlatformDatum,
  SkuComponentDatum,
} from "./utils";

const FORM_ID = "sku-form";

const skuProductLineFieldsFragment = gql(/* GraphQL */ `
  fragment SelectedSkuProductLineFields on ProductLine {
    id
    name
    shortName
    nameSeparator
    isInternalSkuIdAppended
    innerCaseQuantity
  }
`);

const skuComponentFieldsFragment = gql(/* GraphQL */ `
  fragment SelectedSkuSkuComponentsFields on Sku {
    id
    skuComponentsList {
      id
      skuId
      skuComponentType
      upc
      grossWeightLbs
      dimensions {
        id
        height
        width
        depth
      }
    }
  }
`);

const selectedSkuOrderingPlatformsField = gql(/* GraphQL */ `
  fragment SelectedSkuOrderingPlatformsField on Sku {
    id
    ...ProductsTableSku
    productLine {
      ...ProductLineSelectOption
    }
    skuBrandOrderingPlatformsList(orderBy: CREATED_AT_ASC) {
      id
      itemCode
      wholesaleUnitPrice
      brandOrderingPlatform {
        orderingPlatformId
        orderingPlatform {
          id
          name
          ...SkuOrderingPlatformsOrderingPlatformDetails
        }
      }
      skuBrandDistributionCentersList(
        orderBy: CREATED_AT_ASC
        filter: {
          brandDistributionCenter: {
            distributionCenter: { name: { isNull: false } }
          }
        }
      ) {
        id
        brandDistributionCenter {
          id
          distributionCenter {
            id
            ...DistributionCenterSelectOption
          }
        }
      }
    }
  }
`);

const selectedSkuFields = gql(/* GraphQL */ `
  fragment SelectedSkuDetailsFields on Sku {
    id
    name
    shortName
    isArchived
    internalSkuId
    unitNetWeight
    unitNetWeightUnit
    grossPalletWeightLbs
    unitCogs
    unitSrp
    productLineId
    productLine {
      id
      ...SelectedSkuProductLineFields
    }
    tiHi {
      id
      ti
      hi
    }
    updatedAt
    ...ArchiveSkuButtonSku
  }
`);

const selectedSkuQuery = gql(/* GraphQL */ `
  query SelectedSkuDetails($skuId: UUID!) {
    sku(id: $skuId) {
      ...SelectedSkuDetailsFields
      ...SelectedSkuSkuComponentsFields
      ...SelectedSkuOrderingPlatformsField
    }
  }
`);

const selectedProductLineQuery = gql(/* GraphQL */ `
  query SelectedSkuProductLine($productLineId: UUID!) {
    productLine(id: $productLineId) {
      id
      ...SelectedSkuProductLineFields
    }
  }
`);

gql(/* GraphQL */ `
  fragment UpdateSkuO2O on Mutation {
    upsertDimensionsById(input: { dimensions: $dimensions }) {
      dimensions {
        id
      }
    }
    upsertTiHisById(input: { tiHis: $tiHis }) {
      tiHis {
        id
      }
    }
  }
`);

gql(/* GraphQL */ `
  fragment UpdateSkuM2M on Mutation {
    upsertSkuComponentsBySkuIdAndSkuComponentType(
      input: { skuComponents: $skuComponents }
    ) {
      query {
        sku(id: $skuId) {
          ...SelectedSkuSkuComponentsFields
        }
      }
    }
    setSkuBrandOrderingPlatformsForSku(
      input: { skuId: $skuId, orderingPlatformData: $orderingPlatformData }
    ) {
      sku {
        ...SelectedSkuOrderingPlatformsField
      }
    }
  }
`);

const updateSkuMutation = gql(/* GraphQL */ `
  mutation UpdateSku(
    $skuId: UUID!
    $patch: SkuPatch!
    $dimensions: [DimensionInput!]!
    $tiHis: [TiHiInput!]!
    $skuComponents: [SkuComponentInput!]!
    $orderingPlatformData: [SetSkuBrandOrderingPlatformsForSkuDatumInput!]!
  ) {
    ...UpdateSkuO2O
    updateSku(input: { id: $skuId, patch: $patch }) {
      sku {
        id
        name
        productLineId
        ...SelectedSkuDetailsFields
        ...ProductsTableSku
      }
    }
    ...UpdateSkuM2M
  }
`);

const createSkuMutation = gql(/* GraphQL */ `
  mutation CreateSku(
    $input: CreateSkuInput!
    $skuId: UUID!
    $dimensions: [DimensionInput!]!
    $tiHis: [TiHiInput!]!
    $skuComponents: [SkuComponentInput!]!
    $orderingPlatformData: [SetSkuBrandOrderingPlatformsForSkuDatumInput!]!
  ) {
    ...UpdateSkuO2O
    createSku(input: $input) {
      sku {
        id
        name
        productLine {
          id
        }
        ...SelectedSkuDetailsFields
        ...ProductsTableSku
      }
    }
    ...UpdateSkuM2M
  }
`);

const processApolloError = makePostgraphileErrorProcessor<SkuPrimitiveInputs>(
  getPostgraphileErrorMatchersForFields(skuFields, {
    type: "Sku",
  }),
);

function resetSkuRelationsCache(cache: ApolloCache<unknown>) {
  const clearNestedListFieldCache = makeClearNestedListFieldCache(cache);
  clearNestedListFieldCache("BrandDistributionCenter", [
    "skuBrandDistributionCenters",
  ]);
  const clearCachedType = makeClearCachedType(cache);
  clearCachedType("PromotionSku", {
    onlyFields: ["skuBrandOrderingPlatform"],
  });
}

type Props = {
  initialData?: SkuInputs;
  isCreateNew?: boolean;
  onArchiveSuccess?: OnArchiveSuccess;
  onClose: () => void;
  onDeleteSuccess: (data: DeleteSkuData) => void;
  onDuplicateSku: (skuData: SkuInputs) => void;
  onNewCancel?: () => void;
  onNewSuccess?: (data: DocumentType<typeof createSkuMutation>) => void;
  skuId?: string;
};

export default function SkuPanel({
  initialData: initialDataRaw,
  isCreateNew = false,
  onArchiveSuccess,
  onClose,
  onDeleteSuccess,
  onDuplicateSku,
  onNewCancel,
  onNewSuccess,
  skuId,
}: Props) {
  const initialData = useMemo(
    () => ({
      tiHi: null,
      skuComponents: getInitialSkuComponents(),
      ...initialDataRaw,
    }),
    [initialDataRaw],
  );

  const formContext = useForm<SkuInputs>({
    resolver: yupResolver(skuSchema),
    defaultValues: initialData,
  });
  const { reset } = formContext;

  const { data, loading: isLoadingData } = useBrandQuery(selectedSkuQuery, {
    skip: !skuId || isCreateNew,
    variables: skuId ? { skuId } : undefined,
    returnPartialData: true,
  });

  const productLineId = formContext.watch("productLineId");

  const { data: productLineData, loading: isLoadingProductLineData } =
    useBrandQuery(selectedProductLineQuery, {
      skip: (!isCreateNew && !data?.sku) || !productLineId,
      variables: productLineId ? { productLineId } : undefined,
      returnPartialData: true,
    });

  const [updateSku, { loading: isSaving }] = useBrandMutation(
    updateSkuMutation,
    { update: resetSkuRelationsCache },
  );

  const [createSku, { loading: isCreating }] = useBrandMutation(
    createSkuMutation,
    {
      update: (cache, result) => {
        resetSkuRelationsCache(cache);

        const newSku = result.data?.createSku?.sku;

        assertPresence(newSku?.productLine?.id);

        updateCachedProductLineSkus(
          cache,
          newSku.productLine,
          UNARCHIVED_STATUSES,
          (existingSkus) => {
            if (!existingSkus) return existingSkus;
            return [...existingSkus, newSku];
          },
        );
      },
      onCompleted: (newSku) => {
        trackEvent("SKU Created");
        if (onNewSuccess) onNewSuccess(newSku);
      },
    },
  );

  useEffect(() => {
    if (isCreateNew) {
      formContext.setFocus(
        initialData?.productLineId ? "name" : "productLineId",
      );
    }
  }, [formContext, isCreateNew, initialData]);

  const productLine = getFragmentData(
    skuProductLineFieldsFragment,
    productLineData?.productLine,
  );

  const skuComponentsByType = useMemo(() => {
    const skuComponents = getFragmentData(
      skuComponentFieldsFragment,
      data?.sku,
    )?.skuComponentsList;
    if (!skuComponents) return {};
    return keyBy(skuComponents, "skuComponentType");
  }, [data]);

  const disabledComponentTypes = useMemo(() => {
    const scts: SkuComponentType[] = [];
    if (
      !productLine?.innerCaseQuantity &&
      !skuComponentsByType[SkuComponentType.INNER]
    ) {
      scts.push(SkuComponentType.INNER);
    }
    return scts;
  }, [productLine, skuComponentsByType]);

  useEffect(() => {
    if (isCreateNew) {
      reset({ ...initialData });
    } else if (data?.sku) {
      const skuData = getFragmentData(selectedSkuFields, data.sku);

      const skuComponents = getInitialSkuComponents().map<SkuComponentDatum>(
        (sc) => {
          const skuComponent = skuComponentsByType?.[sc.skuComponentType];
          if (!skuComponent) return sc;
          return {
            ...sc,
            ...pick(skuComponent, skuComponentFields),
            dimensions: skuComponent.dimensions
              ? pick(skuComponent.dimensions, DIMENSIONS_FIELDS)
              : null,
          };
        },
      );

      const orderingPlatformData =
        getFragmentData(
          selectedSkuOrderingPlatformsField,
          data.sku,
        ).skuBrandOrderingPlatformsList?.reduce<SkuOrderingPlatformDatum[]>(
          (acc, sbop) => {
            const orderingPlatformId =
              sbop.brandOrderingPlatform?.orderingPlatformId ||
              sbop.brandOrderingPlatform?.orderingPlatform?.id;

            if (!orderingPlatformId) return acc;

            const distributionCenterIds =
              sbop.skuBrandDistributionCentersList?.reduce<UUID[]>(
                (dcIds, sbdc) => {
                  const dcId =
                    sbdc.brandDistributionCenter?.distributionCenter?.id;
                  if (!dcId) return dcIds;
                  dcIds.push(dcId);
                  return dcIds;
                },
                [],
              ) || [];

            acc.push({
              orderingPlatformId,
              ...pick(sbop, skuBrandOrderingPlatformFields),
              distributionCenterIds,
            });

            return acc;
          },
          [],
        ) || [];

      reset({
        ...pick(skuData, skuFields),
        skuComponents,
        tiHi: skuData.tiHi ? pick(skuData.tiHi, TI_HI_FIELDS) : null,
        orderingPlatformData,
      });
    }
  }, [data, skuComponentsByType, isCreateNew, initialData, reset]);

  const sku = getFragmentData(selectedSkuFields, data?.sku);

  const validateUpcUniqueness = useValidateUpcUniqueness();

  const [handleValidSubmit, { isLoading }] = useCreateValidSubmitHandler(
    async (
      formData: SkuInputs,
    ): Promise<{
      isCreated: boolean;
      sku: DocumentType<typeof selectedSkuFields>;
    }> => {
      const newSkuId = uuidv4();

      const {
        skuComponents: skuComponentsRaw,
        tiHi: tiHiRaw,
        orderingPlatformData = [],
        ...baseData
      } = formData;

      const skuComponentsByUpc =
        skuComponentsRaw &&
        keyBy(
          skuComponentsRaw.filter((sc) => sc.upc),
          "upc",
        );
      if (skuComponentsByUpc) {
        const upcs = Object.keys(skuComponentsByUpc);

        const duplicateErrorMsg = "Each UPC for each SKU must be unique.";
        const duplicateUpcsFieldErrors = upcs.reduce((acc, upc) => {
          const newErrors = skuComponentsRaw.reduce((acc2, sc, idx) => {
            if (sc.upc === upc) {
              // eslint-disable-next-line no-param-reassign
              acc2[`skuComponents.${idx}.upc`] = [duplicateErrorMsg];
            }
            return acc2;
          }, {} as ErrorsByFieldName<SkuInputs>);

          if (Object.keys(newErrors).length > 1) {
            return { ...acc, ...newErrors };
          }

          return acc;
        }, {} as ErrorsByFieldName<SkuInputs>);

        if (Object.keys(duplicateUpcsFieldErrors).length) {
          setFieldErrorsWithDict(
            formContext.setError,
            duplicateUpcsFieldErrors,
          );
          throw new UserError(duplicateErrorMsg);
        }

        const errorMsg = "UPC is already assigned to another SKU.";
        const validationInputs = upcs.map((upc) => ({
          upc,
          ignoreSkuId: skuId,
        }));
        const upcValidations = await validateUpcUniqueness(validationInputs);
        const fieldErrors = upcValidations.reduce((acc, validation) => {
          if (validation.isUnique) return acc;
          const skuComponent = skuComponentsByUpc[validation.upc];
          assertPresence(skuComponent.skuComponentType);
          const idx = SKU_COMPONENT_TYPES.indexOf(
            skuComponent.skuComponentType,
          );
          acc[`skuComponents.${idx}.upc`] = [errorMsg];
          return acc;
        }, {} as ErrorsByFieldName<SkuInputs>);
        if (Object.keys(fieldErrors).length) {
          setFieldErrorsWithDict(formContext.setError, fieldErrors);
          throw new UserError(errorMsg);
        }
      }

      const oldProductLineId = sku?.productLineId;

      const skuComponents: SkuComponentInput[] = [];
      const dimensions: DimensionInput[] = [];
      const tiHis: TiHiInput[] = [];

      skuComponentsRaw?.forEach((skuComponent) => {
        if (
          !skuComponent.skuComponentType ||
          disabledComponentTypes.includes(skuComponent.skuComponentType)
        ) {
          return;
        }

        const { dimensions: dimensionsRaw, ...skuComponentAttrs } =
          skuComponent;

        assertPresence(skuComponentAttrs.skuComponentType);

        const existingSkuComponent =
          skuComponentsByType?.[skuComponentAttrs.skuComponentType];

        const dimensionsWithId = dimensionsRaw
          ? {
              ...dimensionsRaw,
              id: existingSkuComponent?.dimensions?.id || uuidv4(),
            }
          : null;

        if (dimensionsWithId) {
          dimensions.push(dimensionsWithId);
        }

        skuComponents.push({
          ...skuComponentAttrs,
          skuId: skuId || newSkuId,
          dimensionsId: dimensionsWithId?.id || null,
        });
      });

      const tiHi = tiHiRaw
        ? {
            ...tiHiRaw,
            id: sku?.tiHi?.id || uuidv4(),
          }
        : null;

      if (tiHi) tiHis.push(tiHi);

      const skuData = {
        ...baseData,
        tiHiId: tiHi?.id || null,
      };

      if (skuId && !isCreateNew) {
        const { data: updateData } = await updateSku({
          variables: {
            patch: skuData,
            skuId,
            dimensions,
            tiHis,
            skuComponents,
            orderingPlatformData,
          },
          update: (cache, result) => {
            const updatedSku = result.data?.updateSku?.sku;
            assertPresence(updatedSku);
            if (
              oldProductLineId &&
              updatedSku.productLineId !== oldProductLineId
            ) {
              const clearNestedListFieldCache =
                makeClearNestedListFieldCache(cache);
              clearNestedListFieldCache("ProductLine", ["skus", "skusList"]);
            }
          },
        });
        const updatedSku = updateData?.updateSku?.sku;
        assertPresence(updatedSku);
        return {
          isCreated: false,
          sku: getFragmentData(selectedSkuFields, updatedSku),
        };
      }

      const { data: createData } = await createSku({
        variables: {
          input: { sku: { id: newSkuId, ...skuData } },
          skuId: newSkuId,
          dimensions,
          tiHis,
          skuComponents,
          orderingPlatformData,
        },
      });
      const createdSku = createData?.createSku?.sku;
      assertPresence(createdSku);
      return {
        isCreated: false,
        sku: getFragmentData(selectedSkuFields, createdSku),
      };
    },
    {
      getSuccessToast: (result) => ({
        title: `SKU ${
          result?.isCreated ? "created" : "updated"
        } successfully: ${result?.sku.name}`,
      }),
      processApolloError,
    },
  );

  const [name, shortName, internalSkuId] = formContext.watch([
    "name",
    "shortName",
    "internalSkuId",
  ]);
  const idEditKey = `${skuId}-${sku?.updatedAt}`;

  const isUpcPresentByType = useMemo(
    () =>
      getFragmentData(
        skuComponentFieldsFragment,
        data?.sku,
      )?.skuComponentsList?.reduce<IsUpcPresentByType>((acc, sc) => {
        acc[sc.skuComponentType] = !!sc.upc;
        return acc;
      }, {}),
    [data?.sku],
  );

  return (
    <EditPanelContent
      onClose={onClose}
      description={`${isCreateNew ? "Add New" : "Edit"} SKU`}
      heading={sku?.name}
      headingPlaceholder={isCreateNew ? "New SKU" : undefined}
      isArchived={sku?.isArchived}
      isLoading={isLoadingData}
      footerContent={
        <>
          {onNewCancel && isCreateNew && (
            <Button onClick={onNewCancel} size="sm">
              Cancel
            </Button>
          )}

          {!isCreateNew && !!sku?.isArchived && (
            <DeleteSkuButton sku={sku} onCompleted={onDeleteSuccess} />
          )}

          {!isCreateNew && sku && (
            <ArchiveSkuButton {...{ onArchiveSuccess, sku }} />
          )}

          <Button
            onClick={() => {
              const currentValues = formContext.getValues();
              const duplicateData: SkuInputs = {
                ...currentValues,
                name: `${currentValues.name ?? ""} (copy)`,
              };
              onDuplicateSku(duplicateData);
            }}
            colorScheme="lightPinkButton"
            variant="outline"
            size="sm"
            disabled={isCreateNew}
          >
            Duplicate
          </Button>

          <SaveButton
            isLoading={isLoading || isSaving || isCreating}
            form={FORM_ID}
          >
            {isCreateNew ? "Create" : "Save"}
          </SaveButton>
        </>
      }
    >
      <Form
        id={FORM_ID}
        formContext={formContext}
        onValidSubmit={handleValidSubmit}
        isLoaded={!isLoadingData || !skuId}
      >
        <ControlledFormField
          name="productLineId"
          id="ProductLineSelectedSku-product-line"
          renderInput={({ field }) => <ProductLineSelect {...field} />}
        />

        <FormField name="name" id="ProductLineSelectedSku-sku-name" />

        <NamingOptions
          internalSkuId={internalSkuId}
          isInternalSkuIdAppended={productLine?.isInternalSkuIdAppended}
          isLoading={isLoadingData || isLoadingProductLineData}
          nameSeparator={productLine?.nameSeparator}
          productLineName={productLine?.name}
          productLineShortName={productLine?.shortName}
          skuName={name}
          skuShortName={shortName}
        >
          <FormField name="shortName" placeholder={name || undefined} mt={0} />
        </NamingOptions>

        <FormField
          name="internalSkuId"
          id="ProductLineSelectedSku-internal-sku-id"
          renderInput={({ ref, ...rest }) => (
            <ReadOnlyIdWrapper
              key={idEditKey}
              isValuePresent={!!sku?.internalSkuId}
              renderInput={({ inputRef, isReadOnly }) => (
                <Input
                  {...rest}
                  ref={(c) => {
                    ref(c);
                    inputRef(c);
                  }}
                  isReadOnly={isReadOnly}
                  fontFamily="mono"
                  fontWeight={300}
                />
              )}
            />
          )}
        />

        <InlineFieldsWrapper>
          <ControlledFormField
            name="unitCogs"
            id="ProductLineSelectedSku-unit-cogs"
            renderInput={({ field }) => (
              <NumberInput
                {...field}
                max={100000}
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                allowNegative={false}
                minW="10rem"
              />
            )}
          />

          <ControlledFormField
            name="unitSrp"
            renderInput={({ field }) => (
              <NumberInput
                {...field}
                max={100000}
                decimalScale={2}
                fixedDecimalScale
                prefix="$"
                allowNegative={false}
                minW="10rem"
              />
            )}
          />
        </InlineFieldsWrapper>

        <InlineFieldsWrapper>
          <ControlledFormField
            name="unitNetWeight"
            id="ProductLineSelectedSku-unit-net-weight"
            renderInput={({ field }) => (
              <NumberInput
                {...field}
                max={999}
                decimalScale={4}
                allowNegative={false}
              />
            )}
          />

          <ControlledFormField
            name="unitNetWeightUnit"
            renderInput={({ field }) => (
              <EnumSelect
                {...field}
                enum={WeightUnit}
                labelsByValue={WEIGHT_UNIT_LABELS}
              />
            )}
          />
        </InlineFieldsWrapper>

        <SkuComponents
          {...{ formContext, idEditKey, isUpcPresentByType }}
          disabledSkuComponentTypes={disabledComponentTypes}
        />

        <ControlledFormField
          name="grossPalletWeightLbs"
          renderInput={({ field }) => (
            <InputGroup>
              <NumberInput
                {...field}
                max={9999}
                decimalScale={4}
                allowNegative={false}
                borderTopRightRadius={0}
                borderBottomRightRadius={0}
                minW="5.5em"
              />
              <InputRightAddon px={2}>lbs</InputRightAddon>
            </InputGroup>
          )}
        />

        <TiHiFieldset
          ControlledFormFieldComponent={ControlledFormField}
          fieldNamePrefix="tiHi"
          legendLabel="Ti/Hi"
        />

        <Box my={7}>
          <SkuOrderingPlatforms
            formContext={formContext}
            isLoading={isLoadingData}
          />
        </Box>
      </Form>
    </EditPanelContent>
  );
}
